import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, RefreshCw } from 'lucide-react';
import { downloadCsv, toCsv } from '@/lib/csv';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatCard } from '@/components/admin/StatCard';
import { MembersBrowser } from '@/components/admin/MembersBrowser';
import { AskData } from '@/components/admin/AskData';
import { ActivityTrendsChart } from '@/components/admin/ActivityTrendsChart';
import { EmailAnalytics } from '@/components/admin/EmailAnalytics';
import {
  type AnalyticsPayload,
  type BreakdownItem,
  type CoachingSummary,
  fetchAnalytics,
  type FunnelData,
  type Grain,
  type NotificationFunnel,
} from '@/api/admin-analytics';
import type { EmailGrain } from '@/api/admin-email-analytics';

export const Route = createFileRoute('/admin/analytics')({
  component: AnalyticsPage,
});

const PILLAR_VAR: Record<string, string> = {
  finance: 'hsl(var(--pillar-finance))',
  operations: 'hsl(var(--pillar-operations))',
  management: 'hsl(var(--pillar-management))',
  marketing: 'hsl(var(--pillar-marketing))',
  sales: 'hsl(var(--pillar-sales))',
  culture: 'hsl(var(--pillar-culture))',
};

const RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

function pct(num: number, denom: number): string {
  if (!denom) return '-';
  return `${Math.round((num / denom) * 100)}%`;
}

/** Download the time-series as a CSV (client-only; admins open it in Excel). */
function exportCsv(d: AnalyticsPayload) {
  // completions = course completions; lessons_completed = lesson completions. Both exported.
  const header = ['bucket', 'signups', 'engagement', 'alfie_messages', 'completions', 'lessons_completed', 'diagnostics'];
  const rows = d.series.map((p) => [
    p.bucket,
    p.signups,
    p.engagement,
    p.alfie_messages,
    p.completions,
    p.lessons_completed,
    p.diagnostics,
  ]);
  downloadCsv(
    `analytics-${d.range.from.slice(0, 10)}_${d.range.to.slice(0, 10)}.csv`,
    toCsv(header, rows),
  );
}

const NOT_PAYING_FILL = 'hsl(var(--muted-foreground))';

/**
 * Horizontal bar list for categorical breakdowns (tokenised, no Recharts). When
 * the items carry a paying / not_paying split, each bar becomes two segments
 * (paying in the primary colour, not paying muted) sized against the same max,
 * with a small legend above the list; the value column stays the total.
 */
function BarList({ items, color }: { items: BreakdownItem[]; color?: (label: string) => string }) {
  if (!items?.length) {
    return <p className="text-xs text-muted-foreground">No data in range.</p>;
  }
  const max = Math.max(...items.map((i) => i.value), 1);
  const split = items.some((i) => i.paying != null || i.not_paying != null);
  return (
    <div className="space-y-1.5">
      {split && (
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: 'hsl(var(--primary))' }}
              aria-hidden
            />
            Paying
          </span>
          <span className="flex items-center gap-1" title="Free and complimentary accounts">
            <span
              className="inline-block h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: NOT_PAYING_FILL, opacity: 0.45 }}
              aria-hidden
            />
            Not paying
          </span>
        </div>
      )}
      {/* Keyed by position: a label is a display name (a tier name, say), and
          nothing stops two rows sharing one. The list is a ranked snapshot
          that is replaced wholesale, never reordered in place. */}
      {items.map((i, idx) => (
        <div
          key={idx}
          className="flex items-center gap-2 text-sm"
          title={split ? `Paying ${i.paying ?? 0} · Not paying ${i.not_paying ?? 0}` : undefined}
        >
          <span className="w-28 shrink-0 truncate capitalize text-muted-foreground" title={i.label}>
            {i.label}
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
            {split ? (
              <div className="flex h-full">
                <div
                  className="h-full"
                  style={{
                    width: `${((i.paying ?? 0) / max) * 100}%`,
                    backgroundColor: 'hsl(var(--primary))',
                  }}
                />
                <div
                  className="h-full"
                  style={{
                    width: `${((i.not_paying ?? 0) / max) * 100}%`,
                    backgroundColor: NOT_PAYING_FILL,
                    opacity: 0.45,
                  }}
                />
              </div>
            ) : (
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(i.value / max) * 100}%`,
                  backgroundColor: color?.(i.label) ?? 'hsl(var(--primary))',
                }}
              />
            )}
          </div>
          <span className="w-10 shrink-0 text-right tabular-nums text-foreground">{i.value}</span>
        </div>
      ))}
    </div>
  );
}

function DomainCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function MiniStat(
  { label, value, title }: { label: string; value: string | number; title?: string },
) {
  return (
    <div className="flex items-baseline justify-between" title={title}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

// Builder Journey - cumulative lifetime stages (not strict drop-off; later stages
// are not subsets). Colours map to the fixed chart palette (§I), never reshuffled.
const FUNNEL_STAGES: { key: keyof FunnelData; label: string; hint: string; color: string }[] = [
  { key: 'acquire', label: 'Acquire', hint: 'all accounts', color: 'hsl(var(--chart-1))' },
  { key: 'activate', label: 'Activate', hint: 'signed in + first step', color: 'hsl(var(--chart-2))' },
  { key: 'learn', label: 'Learn', hint: 'any learning touch', color: 'hsl(var(--chart-3))' },
  { key: 'progress', label: 'Progress', hint: 'completed / passed', color: 'hsl(var(--chart-4))' },
  { key: 'engage', label: 'Engage', hint: 'posts / msgs / Alfie', color: 'hsl(var(--chart-5))' },
  { key: 'reengage', label: 'Re-engage', hint: 'opened a notification', color: 'hsl(var(--chart-6))' },
  { key: 'advance', label: 'Advance', hint: 'above entry tier', color: 'hsl(var(--chart-1))' },
];

/** Cumulative Builder Journey funnel - horizontal bars sized vs the Acquire total. */
function FunnelPanel({ funnel }: { funnel: FunnelData }) {
  const max = Math.max(funnel.acquire, 1);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Builder Journey funnel</CardTitle>
        <p className="text-xs text-muted-foreground">Lifetime - date range does not apply.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {FUNNEL_STAGES.map((st) => {
          const value = funnel[st.key];
          return (
            <div key={st.key} className="flex items-center gap-2 text-sm">
              <span className="w-24 flex-shrink-0 text-muted-foreground">{st.label}</span>
              <div className="h-5 flex-1 overflow-hidden rounded-md bg-secondary">
                <div
                  className="flex h-full items-center justify-end rounded-md px-1.5 text-[11px] font-medium text-background"
                  style={{ width: `${Math.max((value / max) * 100, 6)}%`, backgroundColor: st.color }}
                >
                  {value}
                </div>
              </div>
              <span className="w-28 flex-shrink-0 text-right text-[11px] text-muted-foreground">
                {pct(value, max)} · {st.hint}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * The coaching + roadmap feature (hermes migration 631).
 *
 * Three questions, in the order they are worth asking:
 *   1. Does the card get in front of anyone?      prescribed → seen
 *   2. Do they answer it?                          seen → answered, split by what they said
 *   3. Does it change what they DO?                step clicks — the attribution number
 *
 * `Acted on` is the ratio that matters and the one that did not exist before: a step click is
 * a lesson or template opened FROM a move. Chat exposures sit apart from `seen` on purpose —
 * the chat hands the move to the model rather than showing it, so counting it as an
 * impression would flatter every rate on this card.
 */
function CoachingPanel({ c }: { c: CoachingSummary }) {
  const steps = [
    { label: 'Prescribed', value: c.prescribed_window },
    { label: 'Seen', value: c.seen_window },
    { label: 'Answered', value: c.answered_window },
    { label: 'Acted on', value: c.step_clicks_window },
  ];
  const max = Math.max(c.prescribed_window, 1);
  return (
    <DomainCard title="Coaching & Roadmap">
      <div className="flex gap-3">
        <MiniStatBlock label="Answer rate" value={pct(c.answered_window, c.seen_window)} />
        <MiniStatBlock label="Accept rate" value={pct(c.accepted_window, c.answered_window)} />
      </div>
      <div className="space-y-1.5 pt-1">
        {steps.map((s) => (
          <div key={s.label} className="flex items-center gap-2 text-sm">
            <span className="w-20 flex-shrink-0 text-muted-foreground">{s.label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full"
                style={{ width: `${(s.value / max) * 100}%`, backgroundColor: 'hsl(var(--chart-3))' }}
              />
            </div>
            <span className="w-10 flex-shrink-0 text-right tabular-nums text-foreground">{s.value}</span>
          </div>
        ))}
      </div>
      <MiniStat
        label="Done"
        value={c.done_window}
        title="Answered 'I've done it' — the follow-up is what confirms it"
      />
      <MiniStat label="Declined" value={c.declined_window} />
      <MiniStat label="Follow-ups answered" value={c.followups_answered_window} />
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-muted-foreground" title="Follow-up window passed, still unanswered">
          Follow-ups overdue
        </span>
        <span
          className="text-sm font-semibold tabular-nums"
          style={{ color: c.followups_overdue > 0 ? 'hsl(var(--warning))' : 'hsl(var(--success))' }}
        >
          {c.followups_overdue}
        </span>
      </div>
      <MiniStat
        label="In chat"
        value={c.chat_exposures_window}
        title="The move reached Alfie's chat context. Not an impression — excluded from Seen."
      />
      <div className="flex items-baseline justify-between">
        <span
          className="text-sm text-muted-foreground"
          title="A re-ask answer stored but whose recalculation threw — the score is stale until the nightly sweep"
        >
          Stale write-backs
        </span>
        <span
          className="text-sm font-semibold tabular-nums"
          style={{ color: c.write_back_recalc_failed > 0 ? 'hsl(var(--danger))' : 'hsl(var(--success))' }}
        >
          {c.write_back_recalc_failed}
        </span>
      </div>
      <div className="pt-1">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Where they saw it
        </p>
        <BarList items={c.by_surface} />
      </div>
      <div className="pt-1">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          By move type
        </p>
        <BarList items={c.by_move_type} />
      </div>
      <div className="pt-1">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          By pillar
        </p>
        <BarList items={c.by_pillar} />
      </div>
      {c.by_decline_reason.length > 0 && (
        <div className="pt-1">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Why they declined
          </p>
          <BarList items={c.by_decline_reason} />
        </div>
      )}
      {c.by_followup_result.length > 0 && (
        <div className="pt-1">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            How it went
          </p>
          <BarList items={c.by_followup_result} />
        </div>
      )}
    </DomainCard>
  );
}

/** Notification delivery funnel - sent → delivered → shown → clicked → read. */
function NotificationPanel({ n }: { n: NotificationFunnel }) {
  const steps = [
    { label: 'Sent', value: n.sent },
    { label: 'Delivered', value: n.delivered },
    { label: 'Shown', value: n.shown },
    { label: 'Clicked', value: n.clicked },
    { label: 'Read', value: n.read },
  ];
  const max = Math.max(n.sent, 1);
  const ctr = pct(n.clicked, n.sent);
  const openRate = pct(n.read, n.sent);
  return (
    <DomainCard title="Notifications & Push">
      <div className="flex gap-3">
        <MiniStatBlock label="Open rate" value={openRate} />
        <MiniStatBlock label="Click rate" value={ctr} />
      </div>
      <div className="space-y-1.5 pt-1">
        {steps.map((s) => (
          <div key={s.label} className="flex items-center gap-2 text-sm">
            <span className="w-20 flex-shrink-0 text-muted-foreground">{s.label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full"
                style={{ width: `${(s.value / max) * 100}%`, backgroundColor: 'hsl(var(--chart-6))' }}
              />
            </div>
            <span className="w-10 flex-shrink-0 text-right tabular-nums text-foreground">{s.value}</span>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between pt-1">
        <span className="text-sm text-muted-foreground">Active subscriptions</span>
        <span className="text-sm font-semibold tabular-nums text-foreground">{n.subscriptions_active}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-muted-foreground">Push failures</span>
        <span
          className="text-sm font-semibold tabular-nums"
          style={{ color: n.push_failed > 0 ? 'hsl(var(--danger))' : 'hsl(var(--success))' }}
        >
          {n.push_failed}
        </span>
      </div>
      {n.by_type.length > 0 && (
        <div className="pt-1">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            By type (range)
          </p>
          <BarList items={n.by_type} color={() => 'hsl(var(--chart-6))'} />
        </div>
      )}
    </DomainCard>
  );
}

function MiniStatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 rounded-md bg-secondary px-2.5 py-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-bold tabular-nums text-foreground">{value}</p>
    </div>
  );
}

function AnalyticsPage() {
  const [rangeDays, setRangeDays] = useState(30);
  const [grain, setGrain] = useState<Grain>('day');
  // Lazy gate: the Email tab's query only runs after the tab is first opened.
  const [emailOpened, setEmailOpened] = useState(false);

  const { from, to } = useMemo(() => {
    const now = new Date();
    const f = new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    return { from: f.toISOString(), to: now.toISOString() };
  }, [rangeDays]);

  // The email endpoint supports day/week only - map the page's month grain to week.
  const emailGrain: EmailGrain = grain === 'day' ? 'day' : 'week';

  const query = useQuery({
    queryKey: ['admin-analytics', rangeDays, grain],
    queryFn: () => fetchAnalytics({ from, to, grain }),
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        {/* Header + controls */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground">Platform Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Cross-domain health across members, learning, community, Alfie, business & ops.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>{o.label}</option>
              ))}
            </select>
            <select
              value={grain}
              onChange={(e) => setGrain(e.target.value as Grain)}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
            <button
              onClick={() => query.data && exportCsv(query.data)}
              disabled={!query.data}
              className="rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
              title="Export time-series CSV"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => query.refetch()}
              className="rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:text-foreground"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <Tabs
          defaultValue="overview"
          className="space-y-4"
          onValueChange={(v) => {
            if (v === 'email') setEmailOpened(true);
          }}
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="ask">Ask the data</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {query.isLoading && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-72" />
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-48" />)}
                </div>
              </div>
            )}

            {query.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {query.error instanceof Error ? query.error.message : 'Failed to load analytics.'}
                </AlertDescription>
              </Alert>
            )}

            {query.data && <AnalyticsContent d={query.data} />}
          </TabsContent>

          <TabsContent value="members">
            <MembersBrowser from={from} to={to} />
          </TabsContent>

          <TabsContent value="email" className="space-y-6">
            <EmailAnalytics from={from} to={to} grain={emailGrain} enabled={emailOpened} />
          </TabsContent>

          <TabsContent value="ask">
            <AskData from={from} to={to} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AnalyticsContent({ d }: { d: AnalyticsPayload }) {
  const { summary: s, breakdowns: b, series } = d;
  const completionRate = pct(s.learning.completed_all, s.learning.enrollments_total);
  const passRate = pct(s.learning.passed_all, s.learning.attempts_all);
  const activationRate = pct(s.members.activated, s.members.total_users);
  const abandonRate = pct(s.business.abandoned_all, s.business.started_all);
  const alfieFailRate = pct(s.alfie.failed_all, s.alfie.messages_all);

  return (
    <div className="space-y-6">
      {/* Hero KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Active members" value={s.members.active_window} hint="signed-in, in range" />
        <StatCard label="New signups" value={s.members.new_signups_window} hint="in range" />
        <StatCard label="Completion rate" value={completionRate} hint="course enrolments" />
        <StatCard label="Engagement" value={s.community.posts_window + s.community.messages_window} hint="posts + messages" />
        <StatCard label="Alfie messages" value={s.alfie.messages_window} hint="in range" />
        <StatCard
          label="Transcoding backlog"
          value={s.ops.transcoding_backlog}
          tone={s.ops.transcoding_backlog > 0 ? 'warning' : 'success'}
          hint="media not ready"
        />
      </div>

      {/* Trends */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Activity trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityTrendsChart series={series} />
        </CardContent>
      </Card>

      {/* Engagement funnel (cumulative) */}
      <FunnelPanel funnel={d.funnel} />

      {/* Domain detail grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <DomainCard title="Members & Access">
          <MiniStat label="Active memberships" value={s.members.active} />
          <MiniStat label="Paying" value={s.members.paying_active} />
          <MiniStat
            label="Not paying"
            value={s.members.not_paying_active}
            title="Free and complimentary accounts"
          />
          <MiniStat label="Activation rate" value={activationRate} />
          <div className="pt-1">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Tier mix
            </p>
            <BarList items={b.tiers} />
          </div>
        </DomainCard>

        <DomainCard title="Learning & Content">
          <MiniStat label="Enrolments" value={s.learning.enrollments_total} />
          <MiniStat label="Completion rate" value={completionRate} />
          <MiniStat label="Assessment pass rate" value={passRate} />
          <MiniStat label="Certificates (range)" value={s.learning.certificates_window} />
          <div className="pt-1">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Courses by pillar
            </p>
            <BarList items={b.content_pillars} color={(l) => PILLAR_VAR[l] ?? 'hsl(var(--muted-foreground))'} />
          </div>
        </DomainCard>

        <DomainCard title="Community & Engagement">
          <MiniStat label="Posts (range)" value={s.community.posts_window} />
          <MiniStat label="Messages (range)" value={s.community.messages_window} />
          <MiniStat label="Points awarded" value={s.community.points_window} />
          <MiniStat label="Upcoming events" value={s.community.events_upcoming} />
          <div className="pt-1">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Point sources
            </p>
            <BarList items={b.point_sources} />
          </div>
        </DomainCard>

        <DomainCard title="Alfie / AI">
          <MiniStat label="Threads (all-time)" value={s.alfie.threads_total} />
          <MiniStat label="Messages (range)" value={s.alfie.messages_window} />
          <MiniStat label="Insights (range)" value={s.alfie.insights_window} />
          <MiniStat label="Voice sessions (range)" value={s.alfie.voice_window} />
          <div className="flex items-baseline justify-between pt-1">
            <span className="text-sm text-muted-foreground">Failure rate</span>
            <span
              className="text-sm font-semibold tabular-nums"
              style={{
                color: s.alfie.failed_all > 0 ? 'hsl(var(--warning))' : 'hsl(var(--success))',
              }}
            >
              {alfieFailRate}
            </span>
          </div>
        </DomainCard>

        <DomainCard title="Business (BSS / Prime Path)">
          <MiniStat label="Diagnostics started (range)" value={s.business.diag_started_window} />
          <MiniStat label="Diagnostics completed (range)" value={s.business.diag_completed_window} />
          <MiniStat label="Abandon rate (all-time)" value={abandonRate} />
          <MiniStat label="Avg BSS score" value={s.business.avg_bss ?? '-'} />
          <div className="pt-1">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Lifecycle stages
            </p>
            <BarList items={b.lifecycle} />
          </div>
        </DomainCard>

        <DomainCard title="Platform & Ops Health">
          <MiniStat label="Media ready" value={s.ops.media_ready} />
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Transcoding backlog</span>
            <span
              className="text-sm font-semibold tabular-nums"
              style={{ color: s.ops.transcoding_backlog > 0 ? 'hsl(var(--warning))' : 'hsl(var(--success))' }}
            >
              {s.ops.transcoding_backlog}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Pipeline failures</span>
            <span
              className="text-sm font-semibold tabular-nums"
              style={{ color: s.ops.pipeline_failed > 0 ? 'hsl(var(--danger))' : 'hsl(var(--success))' }}
            >
              {s.ops.pipeline_failed}
            </span>
          </div>
          <MiniStat label="Embeddings total" value={s.ops.embeddings_total} />
          <div className="pt-1">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Embeddings by type
            </p>
            <BarList items={b.embeddings} />
          </div>
        </DomainCard>

        <CoachingPanel c={d.coaching} />

        <NotificationPanel n={d.notifications} />

        <DomainCard title="Org leaderboard">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Top orgs · members / active (range)
          </p>
          {b.orgs.length === 0
            ? <p className="text-xs text-muted-foreground">No organisations.</p>
            : (
              <div className="space-y-1.5">
                {/* Org names are not unique (two orgs can share a name) and the
                    payload carries no id, so key by rank. */}
                {b.orgs.map((o, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-foreground" title={o.label}>{o.label}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {o.value} <span className="text-foreground">/ {o.active}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
        </DomainCard>
      </div>
    </div>
  );
}
